import React, { useEffect, useState, useContext } from "react"
import ReactDOM from "react-dom"
import { NavLink } from "react-router-dom"
import { X } from "lucide-react"
import { db, auth } from "../../../utils/firebase"
import { AlertsContext } from "../../../utils/providers/alerts"
import firebase from "firebase"
import moment from "moment"

/**
 * UI components
 */
import Table from "../../../components/structure/table/table"
import Select from "../../../components/ui/select/select"
import Button from "../../../components/ui/button/button"
import Textarea from "../../../components/ui/inputs/textarea"
import { LoadingIcon } from "../../../utils/svgs"

/**
 * The same activity feed used on the enquiry and booking pages. Embedded in a
 * modal here so agents can log calls/notes against a returning booking before
 * deciding whether to reopen it. Reads/writes the booking's `return_activity`
 * subcollection (kept separate from the booking's own `activity`).
 */
import ActivityFeed from "../../enquiry/_components/activity/feed"

/**
 * How far back a return date can be for a traveller to still appear in the list
 * (~1.5 months). We show everyone who has returned home between this many days
 * ago and yesterday, so recently-returned travellers aren't missed.
 */
const RETURNED_WITHIN_DAYS = 45

/**
 * How far back to query bookings by check in. The return date is the earliest
 * check in plus the total nights, so the check in always falls before the
 * return - this adds a generous trip-length buffer on top of the return window
 * to comfortably cover even long multi-hotel trips while keeping the query small.
 */
const CHECK_IN_LOOKBACK_DAYS = RETURNED_WITHIN_DAYS + 90

/**
 * Pull the seconds out of a Firestore timestamp regardless of whether it has
 * come from a live snapshot (.seconds) or a cached/serialised read (._seconds).
 */
const getSeconds = (timestamp) => timestamp?._seconds || timestamp?.seconds || 0

/**
 * Format a number as a GBP amount, matching the main bookings view.
 */
const formatCurrency = (value) =>
	Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

/**
 * Derive the total holiday cost, profit margin and margin percentage for a
 * booking, mirroring the logic used on the main bookings view.
 */
const calculateCost = (booking) => {
	const totalCost =
		booking.margins?.customer_price ??
		(booking.booking_type === "DIRECT" ? booking.total_cost : booking.enquiry?.quoted) ??
		0
	const margin = booking.margins?.profit ?? 0
	const marginPercent = Number(totalCost) > 0 ? ((Number(margin) / Number(totalCost)) * 100).toFixed(1) : null
	return { totalCost, margin, marginPercent }
}

/**
 * Work out the return date for a booking from its hotels, mirroring the logic
 * used on the stats export: earliest hotel check in + the sum of every hotel's
 * nights. Returns a moment for the return day, or null when we can't tell.
 */
const calculateReturn = (hotels) => {
	let firstCheckIn = null
	let totalNights = 0

	hotels.forEach((hotel) => {
		totalNights += hotel.nights || 0
		const checkIn = getSeconds(hotel.check_in)
		if (checkIn && (!firstCheckIn || checkIn < firstCheckIn)) {
			firstCheckIn = checkIn
		}
	})

	if (!firstCheckIn || totalNights <= 0) return { firstCheckIn: null, totalNights: 0, returnMoment: null }

	return {
		firstCheckIn,
		totalNights,
		returnMoment: moment(firstCheckIn, "X").add(totalNights, "days"),
	}
}

/**
 * Returning travellers tab. Lists every booking belonging to the selected agent
 * (their own, for non-admins) that returned home in the last ~1.5 months, so the
 * agent can open a fresh enquiry to win the trip again next year.
 */
export default function ReturnTravellers(props) {
	const { isAdmin, currentUid, maskDetails, searchTerm, updateCount, openEnquiry } = props

	const { pushAlert } = useContext(AlertsContext)

	const [agents, setAgents] = useState({})
	const [selectedAgent, setSelectedAgent] = useState(isAdmin ? "" : currentUid || "")
	const [loading, setLoading] = useState(false)
	const [rows, setRows] = useState([])
	const [opening, setOpening] = useState({})

	/**
	 * The row currently being closed off ("don't reopen"), the reason being
	 * typed for it, and whether the write is in flight. A non-null target means
	 * the dismiss modal is open.
	 */
	const [dismissTarget, setDismissTarget] = useState(null)
	const [dismissReason, setDismissReason] = useState("")
	const [dismissing, setDismissing] = useState(false)

	/**
	 * The row whose activity-log modal is open (null = closed). Notes logged in
	 * the modal are stored against the booking's `return_activity` subcollection,
	 * letting the agent track calls/emails before deciding whether to reopen.
	 */
	const [noteTarget, setNoteTarget] = useState(null)

	/**
	 * The agent whose returning travellers we are actually showing. Non-admins
	 * are pinned to their own UID; admins choose one from the dropdown.
	 */
	const effectiveAgent = isAdmin ? selectedAgent : currentUid || ""

	/**
	 * Load the list of agents for the admin dropdown - everyone who can have
	 * enquiries/bookings assigned to them (the "can be assigned" flag).
	 */
	useEffect(() => {
		if (!isAdmin) return

		let isMounted = true
		db.collection("users")
			.where("show_for.assignable", "==", true)
			.orderBy("first_name", "asc")
			.get()
			.then((usersSnap) => {
				if (!isMounted) return
				const agentMap = {}
				usersSnap.forEach((doc) => {
					const { first_name, last_name } = doc.data()
					agentMap[doc.id] = `${first_name || ""} ${last_name || ""}`.trim() || doc.id
				})
				setAgents(agentMap)
			})
			.catch((err) => console.error("Unable to load sales agents", err))

		return () => {
			isMounted = false
		}
	}, [isAdmin])

	/**
	 * Whenever the agent in focus changes, rebuild the list of bookings that
	 * returned yesterday for that agent.
	 */
	useEffect(() => {
		if (!effectiveAgent) {
			setRows([])
			return
		}

		let isMounted = true
		setLoading(true)

		const loadReturningBookings = async () => {
			/**
			 * Travellers count as "returned" once they're home, so the latest
			 * return we show is yesterday; the earliest is RETURNED_WITHIN_DAYS ago.
			 */
			const returnWindowEnd = moment().subtract(1, "day").endOf("day")
			const returnWindowStart = moment().subtract(RETURNED_WITHIN_DAYS, "days").startOf("day")
			const windowStart = firebase.firestore.Timestamp.fromMillis(
				moment(returnWindowEnd).subtract(CHECK_IN_LOOKBACK_DAYS, "days").startOf("day").valueOf()
			)
			const windowEnd = firebase.firestore.Timestamp.fromMillis(returnWindowEnd.valueOf())

			/**
			 * Covered by the existing (agent, removed, check_in) composite index.
			 */
			const bookingDocs = await db
				.collection("bookings")
				.where("agent", "==", effectiveAgent)
				.where("removed", "==", false)
				.where("check_in", ">=", windowStart)
				.where("check_in", "<=", windowEnd)
				.orderBy("check_in")
				.get()

			/**
			 * Drop any booking that's already been closed off ("don't reopen"),
			 * so it stays hidden from the list. Filtered client-side to avoid a
			 * new composite index for a field most bookings won't have.
			 */
			const candidates = bookingDocs.docs
				.map((doc) => ({ id: doc.id, ...doc.data() }))
				.filter((booking) => !booking.return_enquiry_dismissed)

			/**
			 * Pull each candidate's hotels in parallel so we can work out the
			 * actual return date, then keep only those whose return falls inside
			 * our window (returned home in the last ~1.5 months, up to yesterday).
			 */
			const withHotels = await Promise.all(
				candidates.map((booking) =>
					db
						.collection(`bookings/${booking.id}/hotels`)
						.get()
						.then((hotelDocs) => ({ booking, hotels: hotelDocs.docs.map((doc) => doc.data()) }))
						.catch(() => ({ booking, hotels: [] }))
				)
			)

			const returnedTravellers = []
			withHotels.forEach(({ booking, hotels }) => {
				const { firstCheckIn, totalNights, returnMoment } = calculateReturn(hotels)
				if (!returnMoment) return
				if (returnMoment.isBefore(returnWindowStart) || returnMoment.isAfter(returnWindowEnd)) return

				const { totalCost, margin, marginPercent } = calculateCost(booking)

				const bookedSeconds = getSeconds(booking.booked) || getSeconds(booking.created)

				returnedTravellers.push({
					id: booking.id,
					reference: booking.reference || "",
					bookedLabel: bookedSeconds ? moment(bookedSeconds, "X").format("MMM D, YYYY") : "",
					client: booking.client || "",
					location: booking.booked_location || booking.enquiry?.location || "",
					groupSize: Number(booking.golfers || 0) + Number(booking.non_golfers || 0),
					nights: totalNights,
					rounds: (booking.rounds_order || []).length,
					totalCost,
					margin,
					marginPercent,
					firstCheckIn,
					returnSeconds: returnMoment.unix(),
					returnLabel: returnMoment.format("MMM D, YYYY"),
					returnEnquiryOpened: !!booking.return_enquiry_opened,
					returnEnquiryId: booking.return_enquiry_id || "",
				})
			})

			/**
			 * Hydrate client names for display, batching the document id "in"
			 * lookups in tens as Firestore requires.
			 */
			const clientIds = Array.from(new Set(returnedTravellers.map((row) => row.client).filter(Boolean)))
			const clientsById = {}
			const batchSize = 10
			for (let i = 0; i < clientIds.length; i += batchSize) {
				const batch = clientIds.slice(i, i + batchSize)
				const clientDocs = await db
					.collection("clients")
					.where(firebase.firestore.FieldPath.documentId(), "in", batch)
					.get()
				clientDocs.forEach((doc) => {
					const { first_name, last_name, email, phone } = doc.data()
					clientsById[doc.id] = {
						name: `${first_name || ""} ${last_name || ""}`.trim(),
						email: email || "",
						phone: phone || "",
					}
				})
			}

			/**
			 * Count the pre-reopen activity logged against each booking so the
			 * table can show how many calls/notes have been tracked so far.
			 * Firebase v8 has no aggregation count, but these subcollections are
			 * tiny so reading their size directly is cheap.
			 */
			const returnActivityCounts = {}
			await Promise.all(
				returnedTravellers.map((row) =>
					db
						.collection(`bookings/${row.id}/return_activity`)
						.get()
						.then((snap) => {
							returnActivityCounts[row.id] = snap.size
						})
						.catch(() => {
							returnActivityCounts[row.id] = 0
						})
				)
			)

			if (!isMounted) return
			setRows(
				returnedTravellers
					.map((row) => {
						const client = clientsById[row.client] || {}
						return {
							...row,
							clientName: client.name || "",
							clientEmail: client.email || "",
							clientPhone: client.phone || "",
							returnActivityCount: returnActivityCounts[row.id] || 0,
						}
					})
					.sort((a, b) => b.returnSeconds - a.returnSeconds || a.clientName.localeCompare(b.clientName))
			)
			setLoading(false)
		}

		loadReturningBookings().catch((err) => {
			console.error("Unable to load returning travellers", err)
			if (!isMounted) return
			setRows([])
			setLoading(false)
		})

		return () => {
			isMounted = false
		}
	}, [effectiveAgent])

	/**
	 * Filter on the shared quick-search term (client name, location or reference).
	 */
	const term = (searchTerm || "").trim().toLowerCase()
	const visibleRows = term
		? rows.filter((row) =>
			[row.clientName, row.location, row.reference]
				.some((value) => (value || "").toLowerCase().includes(term))
		)
		: rows

	/**
	 * Keep the tab badge in sync with what's on screen.
	 */
	useEffect(() => {
		updateCount(visibleRows.length)
	}, [visibleRows.length, updateCount])

	/**
	 * Create a fresh enquiry for a returning traveller and stamp the booking
	 * with the new enquiry's id so the row flips to "View enquiry". Both writes
	 * go in a single batch.
	 */
	const openReturnEnquiry = async (row) => {
		if (opening[row.id]) return
		setOpening((current) => ({ ...current, [row.id]: true }))

		try {
			/**
			 * Pull any pre-reopen activity logged against the booking (calls,
			 * emails, notes made while deciding) plus each entry's replies, so it
			 * can carry over into the new enquiry's activity feed. Reads must run
			 * before the batch.
			 */
			const returnActivitySnap = await db
				.collection(`bookings/${row.id}/return_activity`)
				.orderBy("created", "asc")
				.get()
				.catch(() => null)
			const carryOver = await Promise.all(
				(returnActivitySnap ? returnActivitySnap.docs : []).map((doc) =>
					db
						.collection(`bookings/${row.id}/return_activity/${doc.id}/replies`)
						.orderBy("created", "asc")
						.get()
						.then((repliesSnap) => ({ data: doc.data(), replies: repliesSnap.docs.map((r) => r.data()) }))
						.catch(() => ({ data: doc.data(), replies: [] }))
				)
			)

			const date_from = firebase.firestore.Timestamp.fromMillis(
				moment(row.firstCheckIn, "X").add(12, "months").valueOf()
			)

			const marginPercentText = row.marginPercent !== null ? row.marginPercent : "0"
			const notes =
				`Created from returning client, booked ${row.bookedLabel || "unknown"}, returned ${row.returnLabel}. ` +
				`Booking ref: ${row.reference || "n/a"}. ` +
				`Holiday cost £${formatCurrency(row.totalCost)}, made £${formatCurrency(row.margin)} margin at ${marginPercentText}%.`

			const enquiryRef = db.collection("enquiries").doc()
			const bookingRef = db.doc(`bookings/${row.id}`)

			const batch = db.batch()
			batch.set(enquiryRef, {
				agent: effectiveAgent || auth.currentUser?.uid || null,
				client: row.client,
				created: firebase.firestore.FieldValue.serverTimestamp(),
				date_from,
				group_size: row.groupSize,
				location: `[Gold Dust]: ${row.location}`,
				nights: row.nights,
				rounds: row.rounds,
				quoted: 0,
				notes,
				site: "SYSTEM",
				inbound_channel: null,
				status: "BEING_PROCESSED",
				is_returning: true,
				source_booking: row.id,
				tracking: {
					utm_source: null,
					utm_campaign: null,
					utm_medium: null,
					utm_term: null,
					utm_content: null,
				},
			})
			batch.set(
				bookingRef,
				{ return_enquiry_opened: true, return_enquiry_id: enquiryRef.id },
				{ merge: true }
			)

			/**
			 * Copy the pre-reopen activity onto the new enquiry, preserving the
			 * original timestamps so the timeline reflects when each call/note
			 * actually happened. Replies are recreated under each new entry. The
			 * originals stay on the booking as its historical record.
			 */
			carryOver.forEach(({ data, replies }) => {
				const entryRef = db.collection(`enquiries/${enquiryRef.id}/activity`).doc()
				batch.set(entryRef, data)
				replies.forEach((reply) => {
					batch.set(entryRef.collection("replies").doc(), reply)
				})
			})

			await batch.commit()

			/**
			 * Flip the row in place: the enquiry now exists, so "Open enquiry"
			 * disables and a "View enquiry" link appears.
			 */
			setRows((current) =>
				current.map((existing) =>
					existing.id === row.id
						? { ...existing, returnEnquiryOpened: true, returnEnquiryId: enquiryRef.id }
						: existing
				)
			)
		} catch (err) {
			console.error("Unable to open returning enquiry", err)
		} finally {
			setOpening((current) => {
				const next = { ...current }
				delete next[row.id]
				return next
			})
		}
	}

	/**
	 * Open the "don't reopen" modal for a row, starting with an empty reason.
	 */
	const openDismissModal = (row) => {
		setDismissTarget(row)
		setDismissReason("")
	}

	/**
	 * Close the modal without saving (ignored while a save is in flight).
	 */
	const closeDismissModal = () => {
		if (dismissing) return
		setDismissTarget(null)
		setDismissReason("")
	}

	/**
	 * Stamp the booking with a dismissal reason so it never reappears in this
	 * list, then drop the row from view. Requires a one-line reason.
	 */
	const confirmDismiss = async () => {
		const reason = dismissReason.trim()
		if (!dismissTarget || !reason || dismissing) return

		setDismissing(true)

		try {
			await db.doc(`bookings/${dismissTarget.id}`).set(
				{
					return_enquiry_dismissed: true,
					return_enquiry_dismissed_reason: reason,
					return_enquiry_dismissed_by: auth.currentUser?.uid || null,
					return_enquiry_dismissed_at: firebase.firestore.FieldValue.serverTimestamp(),
				},
				{ merge: true }
			)

			/**
			 * Pull the row out of the list immediately so it disappears without
			 * waiting for a reload.
			 */
			setRows((current) => current.filter((existing) => existing.id !== dismissTarget.id))

			pushAlert({
				type: "SUCCESS",
				title: "Hidden from return travellers",
			})

			setDismissTarget(null)
			setDismissReason("")
		} catch (err) {
			console.error("Unable to dismiss returning traveller", err)
			pushAlert({
				type: "ERROR",
				title: "Couldn't hide this traveller",
				body: err.message,
			})
		} finally {
			setDismissing(false)
		}
	}

	/**
	 * Open the activity-log modal for a row.
	 */
	const openNoteModal = (row) => setNoteTarget(row)

	/**
	 * Close the modal and re-count that booking's return activity so the table
	 * badge reflects anything just added.
	 */
	const closeNoteModal = () => {
		const bookingId = noteTarget?.id
		setNoteTarget(null)
		if (bookingId) refreshReturnActivityCount(bookingId)
	}

	/**
	 * Re-read a single booking's return-activity count and update its row.
	 */
	const refreshReturnActivityCount = async (bookingId) => {
		try {
			const snap = await db.collection(`bookings/${bookingId}/return_activity`).get()
			setRows((current) =>
				current.map((existing) =>
					existing.id === bookingId ? { ...existing, returnActivityCount: snap.size } : existing
				)
			)
		} catch (err) {
			console.error("Unable to refresh return activity count", err)
		}
	}

	const headings = ["Ref", "Booked", "Name", "Email", "Telephone", "Location", "Group size", "Nights", "Rounds", "Returned", "Total cost", "Margin", "Activity", ""]

	/**
	 * Human-readable bounds of the return window shown beside the table.
	 */
	const rangeStartLabel = moment().subtract(RETURNED_WITHIN_DAYS, "days").format("MMM D, YYYY")
	const rangeEndLabel = moment().subtract(1, "day").format("MMM D, YYYY")

	/**
	 * Mask contact details with asterisks when the user isn't allowed to see
	 * them, matching the convention used on the enquiry rows.
	 */
	const maskContact = (value) => {
		if (!value) return ""
		return maskDetails ? value.replace(/./g, "*") : value
	}

	return (
		<>
			<div className="return-travellers-header">
				{isAdmin && (
					<Select
						label="Sales agent:"
						showLabel={true}
						placeholder="Choose a sales agent"
						options={agents}
						selected={selectedAgent}
						onSelect={({ option }) => setSelectedAgent(option)}
						className="return-travellers-agent-select"
					/>
				)}

				<small className="return-travellers-range">
					Showing return travellers from {rangeStartLabel} to {rangeEndLabel}
				</small>
			</div>

			<Table
				className="enquiries-table"
				headings={headings}>
				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}

				{!loading && visibleRows.map((row) => (
					<tr key={row.id}>
						<td>
							<NavLink to={`booking/${row.id}`}>
								{row.reference || "View booking"}
							</NavLink>
						</td>
						<td>{row.bookedLabel || <small className="no-value">-</small>}</td>
						<td>
							{row.clientName
								? !maskDetails && row.client
									? <NavLink to={`client/${row.client}`}>{row.clientName}</NavLink>
									: row.clientName
								: <small className="no-value">No name set</small>}
						</td>
						<td>{maskContact(row.clientEmail) || <small className="no-value">No email set</small>}</td>
						<td>{maskContact(row.clientPhone) || <small className="no-value">No phone set</small>}</td>
						<td>{row.location || <small className="no-value">-</small>}</td>
						<td>{row.groupSize}</td>
						<td>{row.nights}</td>
						<td>{row.rounds}</td>
						<td>{row.returnLabel}</td>
						<td>£{formatCurrency(row.totalCost)}</td>
						<td>
							£{formatCurrency(row.margin)}
							{row.marginPercent !== null && <small className="no-value"> ({row.marginPercent}%)</small>}
						</td>
						<td>
							{row.returnActivityCount > 0
								? <span className="return-activity-count">{row.returnActivityCount}</span>
								: <small className="no-value">-</small>}
						</td>
						<td className="is-button">
							<div className="return-travellers-actions">
								<Button
									label="+ Note"
									type="OUTLINE"
									xsSmall={true}
									onClick={() => openNoteModal(row)}
								/>
								{row.returnEnquiryOpened && row.returnEnquiryId && (
									<Button
										label="View enquiry"
										type="OUTLINE"
										xsSmall={true}
										onClick={() => openEnquiry(row.returnEnquiryId)}
									/>
								)}
								{!row.returnEnquiryOpened && (
									<Button
										label="Don't reopen"
										type="OUTLINE"
										xsSmall={true}
										onClick={() => openDismissModal(row)}
									/>
								)}
								<Button
									label="Open enquiry"
									xsSmall={true}
									loading={!!opening[row.id]}
									disabled={!!opening[row.id] || row.returnEnquiryOpened}
									onClick={() => openReturnEnquiry(row)}
								/>
							</div>
						</td>
					</tr>
				))}

				{!loading && isAdmin && !effectiveAgent && (
					<tr>
						<td colSpan={14}>
							<small>Choose a sales agent to see who has returned home recently.</small>
						</td>
					</tr>
				)}

				{!loading && effectiveAgent && visibleRows.length === 0 && (
					<tr>
						<td colSpan={14}>
							<small>
								{term
									? `No returning travellers found for "${searchTerm.trim()}".`
									: "No travellers have returned home in the last 1.5 months."}
							</small>
						</td>
					</tr>
				)}
			</Table>

			{dismissTarget &&
				ReactDOM.createPortal(
					<div
						className="return-dismiss-modal-backdrop"
						onClick={closeDismissModal}>
						<div
							className="return-dismiss-modal"
							onClick={(event) => event.stopPropagation()}>
							<div className="return-dismiss-modal-header">
								<h3>Don't reopen this enquiry</h3>
							</div>

							<div className="return-dismiss-modal-body">
								<p className="return-dismiss-modal-intro">
									Add a quick line on why{" "}
									{dismissTarget.clientName || "this traveller"}
									{dismissTarget.reference ? ` (${dismissTarget.reference})` : ""} isn't being
									reopened. They'll be hidden from the return travellers list.
								</p>
								<Textarea
									value={dismissReason}
									onChange={(value) => setDismissReason(value)}
									placeholder="e.g. Client has told us they aren't going again next year"
									rows={3}
								/>
							</div>

							<div className="return-dismiss-modal-footer">
								<Button
									type="OUTLINE"
									label="Cancel"
									small={true}
									disabled={dismissing}
									onClick={closeDismissModal}
								/>
								<Button
									label="Hide from list"
									small={true}
									loading={dismissing}
									loadingText="Saving..."
									disabled={!dismissReason.trim()}
									onClick={confirmDismiss}
								/>
							</div>
						</div>
					</div>,
					document.body
				)}

			{noteTarget &&
				ReactDOM.createPortal(
					<div className="return-note-modal-backdrop">
						<div
							className="return-note-modal"
							onClick={(event) => event.stopPropagation()}>
							<div className="return-note-modal-header">
								<div>
									<h3>Activity log</h3>
									<p className="return-note-modal-sub">
										{noteTarget.clientName || "This traveller"}
										{noteTarget.reference ? ` · ${noteTarget.reference}` : ""}
									</p>
								</div>
								<button
									type="button"
									className="return-note-modal-close"
									aria-label="Close"
									onClick={closeNoteModal}>
									<X />
								</button>
							</div>

							<div className="return-note-modal-body">
								<p className="return-note-modal-intro">
									Log calls, emails and notes while you decide whether to reopen. They'll
									carry over to the enquiry when you open it.
								</p>
								<ActivityFeed
									basePath={`bookings/${noteTarget.id}`}
									collectionName="return_activity"
									showHeader={false}
								/>
							</div>
						</div>
					</div>,
					document.body
				)}
		</>
	)
}
